import { useState, useEffect, useMemo, useRef } from 'react';
import { X, User, Building2, Calendar, DollarSign, AlertTriangle, ArrowRight, History, Wallet, Trash2, Pencil } from 'lucide-react';
import { scrollToTop } from '../../hooks/useScrollToTop';
import { apiService } from '../../services/api';

interface Tenant {
  _id: string;
  name: string;
  companyName: string;
  email: string;
  phoneNumber: string;
  currentShop?: {
    _id: string;
    shopNumber: string;
    floor: string;
    size: number;
  };
  rentalHistory: Array<{
    shop: {
      _id: string;
      shopNumber: string;
      floor: string;
    };
    startDate: string;
    endDate?: string;
    status: string;
    monthlyRent: number;
    reasonForLeaving?: string;
    reasonDetails?: string;
  }>;
  payments?: Array<{
    _id: string;
    period?: string;
    amount: number;
    paidAt: string;
    method?: string;
    reference?: string;
    notes?: string;
    recordedBy?: any;
    createdAt?: string;
    updatedAt?: string;
  }>;
  status: string;
}

interface TenantManagementModalProps {
  tenant: Tenant | null;
  onClose: () => void;
  onTenantUpdate: (tenant: Tenant) => void;
}

export default function TenantManagementModal({ tenant, onClose, onTenantUpdate }: TenantManagementModalProps) {
  const [activeTab, setActiveTab] = useState<'details' | 'history' | 'payments' | 'transfer' | 'evict'>('details');
  const [transferData, setTransferData] = useState({
    newShopId: '',
    monthlyRent: '',
    reason: '',
    notes: ''
  });
  const [evictData, setEvictData] = useState({
    reason: '',
    reasonDetails: '',
    notes: ''
  });
  const modalRef = useRef<HTMLDivElement>(null);

  // Scroll to top when modal opens or tab changes
  useEffect(() => {
    scrollToTop(modalRef.current);
  }, [activeTab]);

  // Scroll to top when modal first opens
  useEffect(() => {
    scrollToTop(modalRef.current);
  }, []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [payments, setPayments] = useState<Tenant['payments']>(tenant?.payments || []);
  const [paymentForm, setPaymentForm] = useState({
    period: '',
    amount: '',
    paidAt: new Date().toISOString().slice(0, 10),
    method: 'cash',
    reference: '',
    notes: ''
  });
  const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);

  useEffect(() => {
    if (tenant) {
      setTransferData({
        newShopId: '',
        monthlyRent: '',
        reason: '',
        notes: ''
      });
      setEvictData({
        reason: '',
        reasonDetails: '',
        notes: ''
      });
      setPayments(tenant.payments || []);
      setEditingPaymentId(null);
      setPaymentForm({
        period: '',
        amount: '',
        paidAt: new Date().toISOString().slice(0, 10),
        method: 'cash',
        reference: '',
        notes: ''
      });
    }
  }, [tenant]);

  const paymentSummary = useMemo(() => {
    const totalPaid = (payments || []).reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    return { totalPaid };
  }, [payments]);

  const loadPayments = async () => {
    if (!tenant) return;
    setLoading(true);
    setError('');
    try {
      const resp = await apiService.getTenantPayments(tenant._id);
      if (resp.status === 'success' && resp.data?.payments) {
        setPayments(resp.data.payments);
        onTenantUpdate({ ...(tenant as any), payments: resp.data.payments });
      } else {
        setError(resp.message || 'Failed to load payments');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const submitPayment = async () => {
    if (!tenant) return;
    setLoading(true);
    setError('');

    try {
      const payload = {
        period: paymentForm.period || undefined,
        amount: Number(paymentForm.amount),
        paidAt: paymentForm.paidAt ? new Date(paymentForm.paidAt).toISOString() : undefined,
        method: paymentForm.method as any,
        reference: paymentForm.reference || undefined,
        notes: paymentForm.notes || undefined
      };

      const resp = editingPaymentId
        ? await apiService.updateTenantPayment(tenant._id, editingPaymentId, payload)
        : await apiService.addTenantPayment(tenant._id, payload);

      if (resp.status === 'success' && resp.data?.payments) {
        setPayments(resp.data.payments);
        onTenantUpdate({ ...(tenant as any), payments: resp.data.payments });
        setEditingPaymentId(null);
        setPaymentForm({
          period: '',
          amount: '',
          paidAt: new Date().toISOString().slice(0, 10),
          method: 'cash',
          reference: '',
          notes: ''
        });
      } else {
        setError(resp.message || 'Failed to save payment');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to save payment');
    } finally {
      setLoading(false);
    }
  };

  const startEditPayment = (p: any) => {
    setEditingPaymentId(p._id);
    setPaymentForm({
      period: p.period || '',
      amount: String(p.amount ?? ''),
      paidAt: p.paidAt ? new Date(p.paidAt).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
      method: p.method || 'cash',
      reference: p.reference || '',
      notes: p.notes || ''
    });
    scrollToTop(modalRef.current);
  };

  const removePayment = async (paymentId: string) => {
    if (!tenant) return;
    const ok = window.confirm('Delete this payment record? This cannot be undone.');
    if (!ok) return;

    setLoading(true);
    setError('');
    try {
      const resp = await apiService.deleteTenantPayment(tenant._id, paymentId);
      if (resp.status === 'success' && resp.data?.payments) {
        setPayments(resp.data.payments);
        onTenantUpdate({ ...(tenant as any), payments: resp.data.payments });
        if (editingPaymentId === paymentId) {
          setEditingPaymentId(null);
          setPaymentForm({
            period: '',
            amount: '',
            paidAt: new Date().toISOString().slice(0, 10),
            method: 'cash',
            reference: '',
            notes: ''
          });
        }
      } else {
        setError(resp.message || 'Failed to delete payment');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to delete payment');
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!tenant) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`/api/tenants/${tenant._id}/transfer`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(transferData)
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        onTenantUpdate(data.data.tenant);
        setActiveTab('details');
        setTransferData({ newShopId: '', monthlyRent: '', reason: '', notes: '' });
      } else {
        setError(data.message || 'Transfer failed');
      }
    } catch (err) {
      setError('Transfer failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEvict = async () => {
    if (!tenant) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`/api/tenants/${tenant._id}/evict`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(evictData)
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        onTenantUpdate(data.data.tenant);
        setActiveTab('details');
        setEvictData({ reason: '', reasonDetails: '', notes: '' });
      } else {
        setError(data.message || 'Eviction failed');
      }
    } catch (err) {
      setError('Eviction failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!tenant) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4">
      <div ref={modalRef} className="bg-white rounded-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            Tenant Management - {tenant.name}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-4 sm:px-6">
            {[
              { id: 'details', label: 'Details', icon: User },
              { id: 'history', label: 'History', icon: History },
              { id: 'payments', label: 'Payments', icon: Wallet },
              { id: 'transfer', label: 'Transfer', icon: ArrowRight },
              { id: 'evict', label: 'Evict', icon: AlertTriangle }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-4 sm:p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Name</label>
                      <p className="mt-1 text-sm text-gray-900">{tenant.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <p className="mt-1 text-sm text-gray-900">{tenant.email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone</label>
                      <p className="mt-1 text-sm text-gray-900">{tenant.phoneNumber}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Company Name</label>
                      <p className="mt-1 text-sm text-gray-900">{tenant.companyName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        tenant.status === 'active' ? 'bg-green-100 text-green-800' :
                        tenant.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {tenant.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {tenant.currentShop && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Shop</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-8 w-8 text-primary" />
                      <div>
                        <h4 className="font-medium text-gray-900">Shop {tenant.currentShop.shopNumber}</h4>
                        <p className="text-sm text-gray-600">
                          {tenant.currentShop.floor} Floor • {tenant.currentShop.size.toFixed(1)} sqm
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Rental History</h3>
              <div className="space-y-4">
                {tenant.rentalHistory.map((rental, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">Shop {rental.shop.shopNumber}</h4>
                        <p className="text-sm text-gray-600">{rental.shop.floor} Floor</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        rental.status === 'active' ? 'bg-green-100 text-green-800' :
                        rental.status === 'transferred' ? 'bg-blue-100 text-blue-800' :
                        rental.status === 'evicted' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {rental.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>Start: {new Date(rental.startDate).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>End: {rental.endDate ? new Date(rental.endDate).toLocaleDateString() : 'Ongoing'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        <span>Rent: ${rental.monthlyRent?.toFixed(2) || 'N/A'}</span>
                      </div>
                      {rental.reasonForLeaving && (
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4" />
                          <span>Reason: {rental.reasonForLeaving}</span>
                        </div>
                      )}
                    </div>
                    {rental.reasonDetails && (
                      <div className="mt-2 text-sm text-gray-600">
                        <span className="font-medium">Details:</span> {rental.reasonDetails}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payments Tab */}
          {activeTab === 'payments' && (
            <div className="space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Rent Payments</h3>
                  <p className="text-sm text-gray-600">
                    Admin recorded payments for this tenant. Total paid: <span className="font-medium">${paymentSummary.totalPaid.toFixed(2)}</span>
                  </p>
                </div>
                <button
                  onClick={loadPayments}
                  disabled={loading}
                  className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">
                  {editingPaymentId ? 'Edit Payment' : 'Record New Payment'}
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
                    <input
                      type="text"
                      value={paymentForm.period}
                      onChange={(e) => setPaymentForm({ ...paymentForm, period: e.target.value })}
                      placeholder="e.g. 2026-01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Paid Date</label>
                    <input
                      type="date"
                      value={paymentForm.paidAt}
                      onChange={(e) => setPaymentForm({ ...paymentForm, paidAt: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
                    <select
                      value={paymentForm.method}
                      onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="cash">Cash</option>
                      <option value="bank_transfer">Bank transfer</option>
                      <option value="mobile_money">Mobile money</option>
                      <option value="card">Card</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                    <input
                      type="text"
                      value={paymentForm.reference}
                      onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      rows={2}
                      value={paymentForm.notes}
                      onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-4">
                  {editingPaymentId && (
                    <button
                      onClick={() => {
                        setEditingPaymentId(null);
                        setPaymentForm({
                          period: '',
                          amount: '',
                          paidAt: new Date().toISOString().slice(0, 10),
                          method: 'cash',
                          reference: '',
                          notes: ''
                        });
                      }}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel Edit
                    </button>
                  )}
                  <button
                    onClick={submitPayment}
                    disabled={loading || !paymentForm.amount}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Saving...' : editingPaymentId ? 'Update Payment' : 'Record Payment'}
                  </button>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3">Payment History</h4>
                {(!payments || payments.length === 0) ? (
                  <div className="text-sm text-gray-600 bg-white border border-gray-200 rounded-lg p-4">
                    No payments recorded yet.
                  </div>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-gray-700">
                          <tr>
                            <th className="text-left px-4 py-3">Paid</th>
                            <th className="text-left px-4 py-3">Period</th>
                            <th className="text-left px-4 py-3">Amount</th>
                            <th className="text-left px-4 py-3">Method</th>
                            <th className="text-left px-4 py-3">Reference</th>
                            <th className="text-right px-4 py-3">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {payments.map((p: any) => (
                            <tr key={p._id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">{p.paidAt ? new Date(p.paidAt).toLocaleDateString() : '-'}</td>
                              <td className="px-4 py-3">{p.period || '-'}</td>
                              <td className="px-4 py-3 font-medium">${Number(p.amount || 0).toFixed(2)}</td>
                              <td className="px-4 py-3">{p.method || '-'}</td>
                              <td className="px-4 py-3">{p.reference || '-'}</td>
                              <td className="px-4 py-3">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => startEditPayment(p)}
                                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-700"
                                    title="Edit"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => removePayment(p._id)}
                                    className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                                    title="Delete"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Transfer Tab */}
          {activeTab === 'transfer' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Transfer Tenant</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Transfer this tenant to a different shop. The current rental will be ended and a new one will begin.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Shop ID
                  </label>
                  <input
                    type="text"
                    value={transferData.newShopId}
                    onChange={(e) => setTransferData({ ...transferData, newShopId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter shop ID"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monthly Rent
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={transferData.monthlyRent}
                    onChange={(e) => setTransferData({ ...transferData, monthlyRent: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Transfer
                  </label>
                  <input
                    type="text"
                    value={transferData.reason}
                    onChange={(e) => setTransferData({ ...transferData, reason: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Optional"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={transferData.notes}
                    onChange={(e) => setTransferData({ ...transferData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Additional notes..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setActiveTab('details')}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTransfer}
                  disabled={loading || !transferData.newShopId || !transferData.monthlyRent}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Transferring...' : 'Transfer Tenant'}
                </button>
              </div>
            </div>
          )}

          {/* Evict Tab */}
          {activeTab === 'evict' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Evict Tenant</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Evict this tenant from their current shop. This action will end their rental and mark them as evicted.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Eviction Reason *
                  </label>
                  <input
                    type="text"
                    value={evictData.reason}
                    onChange={(e) => setEvictData({ ...evictData, reason: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter eviction reason"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason Details
                  </label>
                  <textarea
                    value={evictData.reasonDetails}
                    onChange={(e) => setEvictData({ ...evictData, reasonDetails: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Provide additional details about the eviction..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={evictData.notes}
                    onChange={(e) => setEvictData({ ...evictData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Additional notes..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setActiveTab('details')}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEvict}
                  disabled={loading || !evictData.reason}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Evicting...' : 'Evict Tenant'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
